import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./LandmarksQuiz.css";

import sigiriyaImage from "../assets/landmarks/sigiriya_rock.png";
import dambullaTempleImage from "../assets/landmarks/dambulla_temple_image.png";
import jaffnaLibraryImage from "../assets/landmarks/jaffna_public_library.png";
import lotusTowerImage from "../assets/landmarks/lotus_tower.png";
import ellaBridgeImage from "../assets/landmarks/ella_train_bridge_station.jpeg";
import mirissaBeachImage from "../assets/landmarks/mirissa.png";
import landmarksBadge from "../assets/badges/landmarksbadge.png";
import quizResultImg from "../assets/General/quizresult.png";

const landmarkOptions = [
  { key: "sigiriya_rock", title: "Sigiriya Rock", img: sigiriyaImage },
  { key: "golden_temple", title: "Golden Temple", img: dambullaTempleImage },
  { key: "jaffna_library", title: "Jaffna Library", img: jaffnaLibraryImage },
  { key: "lotus_tower", title: "Lotus Tower", img: lotusTowerImage },
  { key: "ella_train_station", title: "Ella Train Station", img: ellaBridgeImage },
  { key: "mirissa_beach", title: "Mirissa Beach", img: mirissaBeachImage },
];

const TOTAL_QUESTIONS = 5;
const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

function shuffle(array) {
  const shuffledItems = [...array];

  // this uses a normal fisher yates shuffle so the quiz order feels different each time
  for (let index = shuffledItems.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffledItems[index], shuffledItems[swapIndex]] = [
      shuffledItems[swapIndex],
      shuffledItems[index],
    ];
  }

  return shuffledItems;
}

function buildQuestions() {
  // only five landmarks are chosen, even though there are more in the list
  const chosenAnswers = shuffle(landmarkOptions).slice(0, TOTAL_QUESTIONS);

  return chosenAnswers.map((correctItem) => {
    const wrongOptions = shuffle(
      landmarkOptions.filter((item) => item.key !== correctItem.key)
    ).slice(0, 2);

    // the correct answer is mixed in with two wrong answers
    const answerOptions = shuffle([correctItem, ...wrongOptions]);

    return {
      id: correctItem.key,
      image: correctItem.img,
      correctKey: correctItem.key,
      correctTitle: correctItem.title,
      options: answerOptions,
      selectedKey: null,
    };
  });
}

function getCurrentUserName() {
  // these keys are checked because different pages may store the user in different ways
  const possibleKeys = [
    "exploreLankaUser",
    "exploreLankaUsername",
    "currentUser",
    "user",
    "username",
    "name",
  ];

  for (const key of possibleKeys) {
    const storedValue = localStorage.getItem(key);
    if (!storedValue) continue;

    try {
      const parsedValue = JSON.parse(storedValue);
      if (typeof parsedValue === "string" && parsedValue.trim()) {
        return parsedValue.trim();
      }
      if (parsedValue?.name) return String(parsedValue.name).trim();
      if (parsedValue?.user?.name) return String(parsedValue.user.name).trim();
      if (parsedValue?.username) return String(parsedValue.username).trim();
    } catch {
      if (storedValue.trim()) return storedValue.trim();
    }
  }

  return "";
}

function HomeButton({ goToPage }) {
  return (
    <button
      className="quizHomeBtn"
      onClick={() => goToPage("/loading", { state: { to: "/home" } })}
      aria-label="Go Home"
      title="Go Home"
      type="button"
    >
      <svg
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="quizHomeBtn__icon"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8 0L0 6V8H1V15H4V10H7V15H15V8H16V6L14 4.5V1H11V2.25L8 0ZM9 10H12V13H9V10Z"
          fill="#ffffff"
        />
      </svg>
    </button>
  );
}

export default function LandmarksQuiz() {
  const goToPage = useNavigate();

  function playYaySound() {
    try {
      const yaySound = new Audio("/audio/yay.mp3");
      yaySound.volume = 1;
      yaySound.play().catch(() => {});
    } catch (error) {
      console.warn("Yay sound failed:", error);
    }
  }

  function playBadgeSound() {
    try {
      const badgeSound = new Audio("/audio/badge.mp3");
      badgeSound.volume = 1;
      badgeSound.play().catch(() => {});
    } catch (error) {
      console.warn("Badge sound failed:", error);
    }
  }

  const [hasStarted, setHasStarted] = useState(false);
  const [questions, setQuestions] = useState(() => buildQuestions());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedKey, setSelectedKey] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [score, setScore] = useState(0);
  const [showBadgePopup, setShowBadgePopup] = useState(false);
  const [savingResult, setSavingResult] = useState(false);
  const [bestScore, setBestScore] = useState(null);

  // refs are used here so the final save gets the latest values after state updates
  const hasSavedResultRef = useRef(false);
  const scoreRef = useRef(0);
  const answersRef = useRef([]);

  const currentQuestion = questions[currentIndex];
  const isFinished = currentIndex >= questions.length;

  const progressText = useMemo(() => {
    return `${Math.min(currentIndex + 1, questions.length)} / ${questions.length}`;
  }, [currentIndex, questions.length]);

  useEffect(() => {
    if (hasStarted) return;

    async function fetchBestScore() {
      const playerName = getCurrentUserName();
      if (!playerName) return;

      try {
        const url = `${API_BASE}/api/best-score/${encodeURIComponent(playerName)}/landmarks_quiz`;
        const response = await fetch(url);
        const scoreData = await response.json();

        if (scoreData?.best_score !== null && scoreData?.best_score !== undefined) {
          setBestScore(scoreData.best_score);
        }
      } catch (error) {
        console.warn("[LandmarksQuiz] fetchBestScore error:", error);
      }
    }

    fetchBestScore();
  }, [hasStarted]);

  function resetQuizState() {
    // keeping this together makes the start and replay resets match each other
    setQuestions(buildQuestions());
    setCurrentIndex(0);
    setSelectedKey(null);
    setShowFeedback(false);
    setScore(0);
    setShowBadgePopup(false);
    hasSavedResultRef.current = false;
    scoreRef.current = 0;
    answersRef.current = [];
  }

  function startQuiz() {
    resetQuizState();
    setHasStarted(true);
  }

  function handleAnswer(selectedOptionKey) {
    if (showFeedback) return;

    const isCorrect = selectedOptionKey === currentQuestion.correctKey;

    if (isCorrect) {
      scoreRef.current += 1;
      setScore((previousScore) => previousScore + 1);
    }

    // these key names are sent to the backend, so the names are kept as they are
    answersRef.current = [
      ...answersRef.current,
      {
        question: currentQuestion.correctTitle,
        correct_key: currentQuestion.correctKey,
        selected_key: selectedOptionKey,
        was_correct: isCorrect,
      },
    ];

    setSelectedKey(selectedOptionKey);
    setShowFeedback(true);

    setQuestions((previousQuestions) =>
      previousQuestions.map((question, index) =>
        index === currentIndex
          ? { ...question, selectedKey: selectedOptionKey }
          : question
      )
    );
  }

  function handleNext() {
    setCurrentIndex((previousIndex) => previousIndex + 1);

    // feedback only resets when there is another question to show
    if (currentIndex < questions.length - 1) {
      setSelectedKey(null);
      setShowFeedback(false);
    }
  }

  function handlePlayAgain() {
    setHasStarted(false);
    resetQuizState();
  }

  useEffect(() => {
    async function saveQuizResult() {
      if (!isFinished || hasSavedResultRef.current) return;

      hasSavedResultRef.current = true;
      setSavingResult(true);
      playYaySound();

      const finalScore = scoreRef.current;
      const finalAnswers = answersRef.current;
      const playerName = getCurrentUserName();

      if (!playerName) {
        console.warn("[LandmarksQuiz] No player name, score not saved.");
        setSavingResult(false);
        return;
      }

      try {
        const payload = {
          name: playerName,
          page_name: "landmarks_quiz",
          score: finalScore,
          total_questions: questions.length,
          badge_key: "landmarks_master",
          answers: finalAnswers,
        };

        const response = await fetch(`${API_BASE}/api/quiz-result`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        let resultData = {};
        try {
          resultData = await response.json();
        } catch {
          resultData = {};
        }

        if (!response.ok) {
          console.error("[LandmarksQuiz] Save failed:", resultData);
          return;
        }

        if (resultData?.best_score !== null && resultData?.best_score !== undefined) {
          setBestScore(resultData.best_score);
        }

        // the badge popup only appears if the backend says this badge is new
        if (resultData?.newly_awarded_badge === true) {
          playBadgeSound();
          setTimeout(() => setShowBadgePopup(true), 500);
        }
      } catch (error) {
        console.error("[LandmarksQuiz] Network error:", error);
      } finally {
        setSavingResult(false);
      }
    }

    saveQuizResult();
  }, [isFinished, questions.length]);

  if (!hasStarted) {
    return (
      <div className="landmarksQuizPage">
        <header className="landmarksQuizHeader">
          <div className="landmarksQuizHeaderTop">
            <HomeButton goToPage={goToPage} />
          </div>

          <h1 className="landmarksQuizTitle">Landmarks Quiz Adventure!</h1>
          <p className="landmarksQuizSubtitle">
            Let&apos;s see how well you know the famous landmarks of Sri Lanka!
          </p>
        </header>

        <main className="landmarksQuizWrap">
          <section className="introPanel">
            {bestScore !== null && (
              <p className="introBestScore">
                Your best score: <strong>{bestScore} / {TOTAL_QUESTIONS}</strong>
              </p>
            )}

            <h2 className="introTitle">How to play</h2>

            <div className="introSteps">
              <div className="introStep">
                <span className="introStepNumber">1</span>
                <p>Look carefully at the landmark picture.</p>
              </div>

              <div className="introStep">
                <span className="introStepNumber">2</span>
                <p>Choose the correct landmark name from the 3 options.</p>
              </div>

              <div className="introStep">
                <span className="introStepNumber">3</span>
                <p>You will answer 5 random landmark questions.</p>
              </div>

              <div className="introStep">
                <span className="introStepNumber">4</span>
                <p>Get 5 out of 5 to unlock the Landmarks Badge!</p>
              </div>
            </div>

            <div className="introActions">
              <button className="startQuizBtn" onClick={startQuiz} type="button">
                Start Quiz
              </button>
            </div>
          </section>
        </main>
      </div>
    );
  }

  if (isFinished) {
    const finalDisplayScore = questions.filter(
      (question) => question.selectedKey === question.correctKey
    ).length;

    return (
      <div className="landmarksQuizPage">
        <header className="landmarksQuizHeader">
          <div className="landmarksQuizHeaderTop">
            <HomeButton goToPage={goToPage} />
          </div>

          <h1 className="landmarksQuizTitle">Landmarks Quiz Adventure!</h1>
        </header>

        <main className="landmarksQuizWrap">
          <section className="resultPanel">
            <img
              src={quizResultImg}
              alt="Quiz result"
              className="quizResultImage"
            />

            <h2 className="quizResultTitle">You finished the quiz!</h2>

            <p className="quizResultScore">
              Your score: <span>{finalDisplayScore}</span> / {questions.length}
            </p>

            {savingResult && (
              <p className="quizSavingText">Saving your score...</p>
            )}

            <div className="quizResultActions">
              <button className="quizActionBtn" onClick={handlePlayAgain} type="button">
                Play Again
              </button>

              <button
                className="quizActionBtn secondary"
                onClick={() => goToPage("/landmarks")}
                type="button"
              >
                Back to Landmarks
              </button>
            </div>
          </section>
        </main>

        {showBadgePopup && (
          <div className="badgeOverlay" onClick={() => setShowBadgePopup(false)}>
            <div
              className="badgePopup"
              onClick={(event) => event.stopPropagation()}
            >
              <h2 className="badgePopupTitle">Badge Unlocked!</h2>
              <p className="badgePopupText">
                You got 5 out of 5 and earned the Landmarks Badge!
              </p>

              <img
                src={landmarksBadge}
                alt="Landmarks badge"
                className="badgeImage"
              />

              <button
                className="quizActionBtn"
                onClick={() => setShowBadgePopup(false)}
                type="button"
              >
                Yay!
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="landmarksQuizPage">
      <header className="landmarksQuizHeader">
        <div className="landmarksQuizHeaderTop">
          <HomeButton goToPage={goToPage} />
        </div>

        <h1 className="landmarksQuizTitle">Landmarks Quiz Adventure!</h1>
        <p className="landmarksQuizSubtitle">
          Pick the correct name for the landmark picture.
        </p>
      </header>

      <div className="landmarksQuizTopBar">
        <span className="quizPill">
          Question: <b>{progressText}</b>
        </span>
        <span className="quizPill">
          Score: <b>{score}</b>
        </span>
      </div>

      <main className="landmarksQuizWrap">
        <section className="questionPanel">
          <p className="quizPromptLabel">Which landmark is this?</p>

          <div className="landmarksQuizImageCard">
            <img
              src={currentQuestion.image}
              alt="Sri Lankan landmark"
              className="landmarksQuizImage"
            />
          </div>

          <div className="quizOptionsGrid">
            {currentQuestion.options.map((option) => {
              const isSelected = selectedKey === option.key;
              const isCorrect = showFeedback && option.key === currentQuestion.correctKey;
              const isWrong =
                showFeedback && isSelected && option.key !== currentQuestion.correctKey;

              return (
                <button
                  key={option.key}
                  className={`quizOptionCard ${isSelected ? "selected" : ""} ${isCorrect ? "correct" : ""} ${isWrong ? "wrong" : ""}`}
                  onClick={() => handleAnswer(option.key)}
                  type="button"
                  disabled={showFeedback}
                >
                  {option.title}
                </button>
              );
            })}
          </div>

          {showFeedback && (
            <div
              className={`quizFeedback ${
                selectedKey === currentQuestion.correctKey ? "good" : "bad"
              }`}
            >
              {selectedKey === currentQuestion.correctKey
                ? "Correct! Great job!"
                : `Oops! The correct answer was ${currentQuestion.correctTitle}.`}
            </div>
          )}

          {showFeedback && (
            <div className="quizNextRow">
              <button className="quizActionBtn" onClick={handleNext} type="button">
                {currentIndex === questions.length - 1 ? "See Results" : "Next Question"}
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}